import {
  Box,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@/shared/ui/display/EmojiIcon';
import { nanoid } from 'nanoid';
import { useAdvancedSearchTheme } from '@/shared/ui/search/AdvancedSearch/theme/useAdvancedSearchTheme';
import { useSimpleSearchTheme } from '@/shared/ui/search/SimpleSearch/theme/useSimpleSearchTheme';
import { useFilterManagement } from '@/shared/ui/search/AdvancedSearch/hooks/useFilterManagement';
import { FilterGroupCard } from '@/shared/ui/search/AdvancedSearch/components/FilterGroupCard';
import { FilterRow } from '@/shared/ui/search/AdvancedSearch/components/FilterRow';
import { FieldSelect } from '@/shared/ui/search/AdvancedSearch/components/FieldSelect';
import { OperatorSelect } from '@/shared/ui/search/AdvancedSearch/components/OperatorSelect';
import { ValueInput } from '@/shared/ui/search/AdvancedSearch/components/ValueInput';
import type { SearchConfig } from '@/shared/ui/search/AdvancedSearch/types/query.types';
import type { SearchQuery } from '@/shared/ui/search/AdvancedSearch/types/query.types';
import type { SortConfig } from '@/shared/ui/search/AdvancedSearch/types/query.types';
import type { FilterOperator } from '@/shared/ui/search/AdvancedSearch/types/field.types';
import type { SortDirection } from '@/shared/ui/search/AdvancedSearch/types/query.types';
import type { SortField } from '@/shared/ui/search/AdvancedSearch/types/sort.types';
import { useRef } from 'react';

interface AdvancedSearchProps {
  config: SearchConfig;
  onSearch: (query: SearchQuery) => void;
  initialQuery?: SearchQuery;
  endAdornment?: React.ReactNode;
}

export function AdvancedSearch({
  config,
  onSearch,
  initialQuery,
  endAdornment,
}: AdvancedSearchProps) {
  const theme = useAdvancedSearchTheme();

  // initialQuery가 없을 때만 쓰는 기본 쿼리. 매 렌더마다 nanoid()로 새 객체를 만들면
  // useFilterManagement의 useEffect([initialQuery])가 매번 setQuery → #185 무한 루프가 난다.
  const fallbackInitialQueryRef = useRef<SearchQuery | null>(null);
  let defaultInitialQuery: SearchQuery | undefined;
  if (initialQuery) {
    defaultInitialQuery = initialQuery;
  } else if (config.defaultSort || config.defaultPageSize) {
    if (!fallbackInitialQueryRef.current) {
      fallbackInitialQueryRef.current = {
        filterGroups: [
          {
            id: nanoid(),
            logic: 'AND',
            filters: [
              {
                id: nanoid(),
                fieldName: '',
                operator: 'EQUALS',
                value: null,
              },
            ],
          },
        ],
        ...(config.defaultSort && { sort: config.defaultSort }),
        ...(config.defaultPageSize && { pagination: { pageSize: config.defaultPageSize } }),
      };
    }
    defaultInitialQuery = fallbackInitialQueryRef.current;
  } else {
    defaultInitialQuery = undefined;
  }

  const {
    query,
    setQuery,
    addFilterGroup,
    removeFilterGroup,
    addFilter,
    removeFilter,
    updateFilter,
  } = useFilterManagement(defaultInitialQuery);

  const handleSortChange = (sortConfig: SortConfig) => {
    setQuery((prev) => ({ ...prev, sort: sortConfig }));
  };

  const handleSortFieldChange = (field: string) => {
    handleSortChange({
      field,
      direction: query.sort?.direction || 'ASC',
    });
  };

  const handleSortDirectionToggle = () => {
    if (!query.sort) return;

    const newDirection: SortDirection = query.sort.direction === 'ASC' ? 'DESC' : 'ASC';
    handleSortChange({
      field: query.sort.field,
      direction: newDirection,
    });
  };

  const handleSearch = () => {
    onSearch(query);
  };

  const simpleSearchTheme = useSimpleSearchTheme();

  return (
    <Box sx={theme.searchContainerSx}>
      <Stack spacing={1}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: simpleSearchTheme.tokens.container.gap,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={addFilterGroup}
            sx={theme.addGroupButtonSx}
          >
            + 조건 그룹 추가
          </Button>

          {config.sortFields && config.sortFields.length > 0 && (
            <>
              <FormControl sx={theme.fieldSelectSx} size="small">
                <InputLabel>정렬 기준</InputLabel>
                <Select
                  value={query.sort?.field || ''}
                  label="정렬 기준"
                  onChange={(e) => handleSortFieldChange(e.target.value)}
                >
                  {config.sortFields.map((field: SortField) => (
                    <MenuItem key={field.name} value={field.name}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {query.sort && (
                <IconButton
                  size="small"
                  onClick={handleSortDirectionToggle}
                  title={query.sort.direction === 'ASC' ? '오름차순' : '내림차순'}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'text.primary',
                    },
                  }}
                >
                  {query.sort.direction === 'ASC' ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </IconButton>
              )}
            </>
          )}

          <Box sx={{ flex: 1 }} />

          <Button variant="contained" onClick={handleSearch} sx={theme.searchButtonSx}>
            검색
          </Button>
          {endAdornment}
        </Box>

        {query.filterGroups.map((group, groupIndex) => {
          const selectedField = (fieldName: string) =>
            config.fields.find((f) => f.name === fieldName);

          return (
            <FilterGroupCard
              key={group.id}
              groupNumber={groupIndex + 1}
              onDelete={
                query.filterGroups.length > 1 ? () => removeFilterGroup(group.id) : undefined
              }
            >
              {group.filters.map((filter) => {
                const field = selectedField(filter.fieldName);
                const availableOperators = field?.operators || [];

                return (
                  <FilterRow key={filter.id} onDelete={() => removeFilter(group.id, filter.id)}>
                    <FieldSelect
                      value={filter.fieldName}
                      fields={config.fields}
                      onChange={(fieldName) => {
                        const newField = config.fields.find((f) => f.name === fieldName);
                        const availableOps = newField?.operators ?? [];
                        const needsOperatorReset =
                          availableOps.length > 0 &&
                          !availableOps.includes(filter.operator as FilterOperator);
                        updateFilter(group.id, filter.id, {
                          fieldName,
                          value: null,
                          ...(needsOperatorReset && { operator: availableOps[0] }),
                        });
                      }}
                      label="필드"
                    />

                    {filter.fieldName && (
                      <>
                        <OperatorSelect
                          value={filter.operator as FilterOperator}
                          operators={availableOperators}
                          onChange={(operator) =>
                            updateFilter(group.id, filter.id, {
                              operator,
                            })
                          }
                          label="조건"
                        />

                        {field && (
                          <ValueInput
                            field={field}
                            value={filter.value}
                            onChange={(value) =>
                              updateFilter(group.id, filter.id, {
                                value,
                              })
                            }
                          />
                        )}
                      </>
                    )}
                  </FilterRow>
                );
              })}

              <Button
                variant="text"
                size="small"
                onClick={() => addFilter(group.id)}
                sx={theme.addFilterButtonSx}
              >
                + 필터 추가
              </Button>
            </FilterGroupCard>
          );
        })}
      </Stack>
    </Box>
  );
}
